import { useState, useEffect, useRef } from "react";
//import LegalNotice from "./LegalNotice";
import {
  Instagram,
  Smile,
  Globe,
  Users,
  Zap,
  BookMarked,
  Sparkles,
  Youtube,
} from "lucide-react";
import InstallAppButton from "./InstallAppButton"; //Facebook
//import { FaXTwitter } from "react-icons/fa6";

// ─── Vybex Logo ──────────────────────────────────────────────────────────────
const VybexLogo = () => {
  const ref = useRef(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Michroma&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const container = ref.current;
    if (!container) return;
    container.innerHTML = ""; // prevent double render in StrictMode
    "VYBEX".split("").forEach((ch, i) => {
      const s = document.createElement("span");
      Object.assign(s.style, {
        fontFamily: "'Michroma', sans-serif",
        background: "linear-gradient(135deg, #00d4ff, #7c3aed, #ec4899)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        opacity: "0",
        transform: "translateY(6px) scale(0.85)",
        display: "inline-block",
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms`,
      });
      s.textContent = ch;
      container.appendChild(s);
      setTimeout(
        () => {
          s.style.opacity = "1";
          s.style.transform = "translateY(0) scale(1)";
        },
        100 + i * 80,
      );
    });
  }, []);
  return (
    <span
      ref={ref}
      style={{
        display: "inline-flex",
        letterSpacing: "0.18em",
        fontSize: "0.9rem",
        background: "#0a0a18",
        padding: "4px 12px",
        borderRadius: "999px",
        border: "1px solid rgba(124,58,237,0.35)",
      }}
    />
  );
};
// ─── Data ─────────────────────────────────────────────────────────────────
const data = {
  hero: {
    tagline: "Because 'What should I watch?' deserves a real answer.",
    sub: "Not an algorithm. Not a list. A recommendation built around you — right now.",
  },
  problem: [
    {
      stat: "40 min",
      label: "Average time wasted deciding what to watch every day",
    },
    {
      stat: "3.5B+",
      label: "People subscribed to streaming platforms globally",
    },
    { stat: "0", label: "Platforms that ask how you FEEL before recommending" },
  ],
  whyUs: [
    {
      icon: <Smile size={22} />,
      title: "Mood-First, Not History-First",
      tag: "Core Differentiator",
      body: "Every other platform recommends based on what you watched last week. We ask how you feel right now — anxious, bored, heartbroken, euphoric — and find what genuinely fits this moment.",
    },
    {
      icon: <Globe size={22} />,
      title: "Platform-Agnostic. Truly.",
      tag: "Unique in Market",
      body: "Netflix only shows Netflix. Prime pushes Prime. We recommend the BEST match — whether it's on Hotstar, YouTube, a cinema near you, or a hidden gem on MUBI. We have no platform loyalty. Only yours.",
    },
    {
      icon: <Users size={22} />,
      title: "Built for Every Viewing Context",
      tag: "All Ages & Occasions",
      body: "Solo binge at midnight. Family movie Sunday. Date night. Watching with friends. We tailor recommendations to who you're watching WITH — not just what you've seen before.",
    },
    {
      icon: <Zap size={22} />,
      title: "5 Questions. One Perfect Pick.",
      tag: "Zero Decision Fatigue",
      body: "No infinite scrolling. No decision paralysis. Answer a few smart questions and get a confident, curated recommendation in seconds. We respect your time.",
    },
    {
      icon: <BookMarked size={22} />,
      title: "Your Watch History, Finally Unified",
      tag: "Cross-Platform Memory",
      body: "Track everything you've watched across every platform — all in one place. No more 'wait, did I already see this?' ever again.",
    },
    {
      icon: <Sparkles size={22} />,
      title: "Gets Smarter the More You Use It",
      tag: "Adaptive Intelligence",
      body: "Our AI learns your taste, your dislikes, your weird guilty pleasures. Over time, recommendations feel less like suggestions and more like a best friend who knows your taste perfectly.",
    },
  ],
  vsCompetitors: [
    {
      feature: "Mood-based recommendation",
      us: true,
      netflix: false,
      google: false,
      letterboxd: false,
    },
    {
      feature: "Platform-agnostic results",
      us: true,
      netflix: false,
      google: "partial",
      letterboxd: "partial",
    },
    {
      feature: "Family / group mode",
      us: true,
      netflix: "partial",
      google: false,
      letterboxd: false,
    },
    {
      feature: "Cross-platform watch history",
      us: true,
      netflix: false,
      google: false,
      letterboxd: true,
    },
    {
      feature: "No decision fatigue UX",
      us: true,
      netflix: false,
      google: false,
      letterboxd: false,
    },
    {
      feature: "Shareable recommendations",
      us: true,
      netflix: false,
      google: false,
      letterboxd: true,
    },
    {
      feature: "Free tier available",
      us: true,
      netflix: false,
      google: true,
      letterboxd: true,
    },
    {
      feature: "Learns your taste over time",
      us: true,
      netflix: true,
      google: "partial",
      letterboxd: "partial",
    },
  ],
  benefits: [
    {
      who: "For the Indecisive",
      benefit:
        "Never waste another evening scrolling. Get a match in under 60 seconds.",
    },
    {
      who: "For Families",
      benefit:
        "One tool that satisfies everyone — from the 8-year-old to the grandparent.",
    },
    {
      who: "For Couples",
      benefit:
        "End the 'you pick / no you pick' loop forever. Let the mood decide.",
    },
    {
      who: "For Explorers",
      benefit:
        "Discover films and shows you'd never find on your own platform's front page.",
    },
    {
      who: "For Binge-Watchers",
      benefit:
        "Track everything you've seen. Build your watchlist. Never re-watch by accident.",
    },
  ],
  monetisation: [
    {
      plan: "Free Forever",
      desc: "Ad-supported. Full mood-based recommendations. Core features for everyone.",
      highlight: false,
    },
    {
      plan: "Cinepass Pro",
      desc: "No ads. Unlimited daily searches. 6 recommendations per search.",
      highlight: true,
    },
    {
      plan: "Family Bundle",
      desc: "Up to 6 profiles. Group watch mode. Parental filters. Shared watchlist.",
      highlight: false,
    },
  ],
};

// ─── Utilities ────────────────────────────────────────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Section Label ────────────────────────────────────────────────────────
const SectionLabel = ({ text, center }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      marginBottom: "1rem",
      justifyContent: center ? "center" : "flex-start",
    }}
  >
    <div
      style={{
        width: "2rem",
        height: "2px",
        background: "linear-gradient(90deg,#667eea,#764ba2)",
        borderRadius: "2px",
      }}
    />
    <span
      style={{
        fontSize: "0.73rem",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: "#7c3aed",
        fontWeight: 700,
      }}
    >
      {text}
    </span>
    {center && (
      <div
        style={{
          width: "2rem",
          height: "2px",
          background: "linear-gradient(90deg,#764ba2,#667eea)",
          borderRadius: "2px",
        }}
      />
    )}
  </div>
);

// ─── Why Us Card ──────────────────────────────────────────────────────────
const WhyUsCard = ({ item, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <FadeIn delay={delay}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: hov
            ? "linear-gradient(135deg,rgba(102,126,234,0.05),rgba(118,75,162,0.08))"
            : "#fff",
          border: hov ? "1.5px solid #c4b5fd" : "1.5px solid #ede9fe",
          borderRadius: "16px",
          padding: "2rem",
          transition: "all 0.28s",
          transform: hov ? "translateY(-4px)" : "none",
          boxShadow: hov
            ? "0 16px 48px rgba(118,75,162,0.1)"
            : "0 2px 12px rgba(0,0,0,0.04)",
          height: "100%",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background:
              "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#7c3aed",
            marginBottom: "1.25rem",
          }}
        >
          {item.icon}
        </div>
        <div
          style={{
            display: "inline-block",
            fontSize: "0.68rem",
            fontWeight: "700",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#7c3aed",
            background: "rgba(102,126,234,0.07)",
            border: "1px solid #c4b5fd",
            borderRadius: "999px",
            padding: "0.2rem 0.7rem",
            marginBottom: "0.9rem",
          }}
        >
          {item.tag}
        </div>
        <h3
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "1.15rem",
            fontWeight: "700",
            color: "#1e1b4b",
            marginBottom: "0.75rem",
            lineHeight: 1.3,
          }}
        >
          {item.title}
        </h3>
        <p
          style={{
            color: "#6b7280",
            lineHeight: 1.75,
            fontSize: "0.9rem",
            margin: 0,
          }}
        >
          {item.body}
        </p>
      </div>
    </FadeIn>
  );
};

// ─── Check Cell ───────────────────────────────────────────────────────────
const CheckCell = ({ val, isUs }) => {
  if (val === true)
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: isUs
            ? "linear-gradient(135deg,#667eea,#764ba2)"
            : "rgba(16,185,129,0.1)",
          border: isUs ? "none" : "1.5px solid #10b981",
        }}
      >
        <span
          style={{
            fontSize: "0.65rem",
            color: isUs ? "#fff" : "#10b981",
            fontWeight: 800,
          }}
        >
          ✓
        </span>
      </span>
    );
  if (val === false)
    return <span style={{ color: "#d1d5db", fontSize: "1.1rem" }}>—</span>;
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: "0.68rem",
        fontWeight: 700,
        color: "#7c3aed",
        background: "rgba(102,126,234,0.08)",
        border: "1px solid #c4b5fd",
        borderRadius: "999px",
        padding: "0.15rem 0.5rem",
      }}
    >
      partial
    </span>
  );
};

// ─── Footer Link ──────────────────────────────────────────────────────────
const FooterLink = ({ href, onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={href || "#"}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: hover ? "#7c3aed" : "#4b5563",
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: hover ? "600" : "400",
        cursor: "pointer",
        transition: "color 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
      }}
    >
      {hover && <span style={{ fontSize: "0.6rem", color: "#a78bfa" }}>▶</span>}
      {children}
    </a>
  );
};

// ─── Social Icon ──────────────────────────────────────────────────────────
const SocialIcon = ({ s }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={s.href}
      target="_blank"
      rel="noopener noreferrer"
      title={s.name}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        border: hover ? `1.5px solid ${s.hoverBg}` : "1.5px solid #ede9fe",
        background: hover ? `${s.hoverBg}14` : "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1rem",
        textDecoration: "none",
        transition: "all 0.2s",
        transform: hover ? "translateY(-2px)" : "none",
      }}
    >
      {s.icon}
    </a>
  );
};

// ─── Inline Footer ────────────────────────────────────────────────────────
const Footer = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();
  const handleNavigation = (view) => {
    if (onNavigate) onNavigate(view);
    else window.location.href = view.startsWith("/") ? view : `/${view}`;
  };
  const socials = [
    {
      name: "Instagram",
      icon: <Instagram size={18} color="#E1306C" />,
      href: "https://www.instagram.com/noryq.ai",
      hoverBg: "#E1306C",
    },

    {
      name: "YouTube",
      icon: <Youtube size={18} color="#aa023aff" />,
      href: "https://youtube.com/@noryq-ai",
      hoverBg: "#FF0000",
    },
  ];
  const quickLinks = [
    { name: "Home", view: "home" },
    { name: "Trending", view: "trending" },
    { name: "Premium", view: "premium" },
    { name: "About Us", href: "/about" },
    { name: "Why Us", href: "/whyus" },
  ];
  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
    { name: "Refund Policy", href: "/refund" },
    { name: "Grievance Officer", href: "/privacy#grievance" },
    { name: "Contact Us", href: "/contact" },
  ];
  return (
    <footer
      style={{
        background: "#fff",
        borderTop: "1px solid #ede9fe",
        padding: "4rem 2rem 2rem",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "3rem",
            marginBottom: "3rem",
          }}
        >
          <div>
            <a
              href="/"
              style={{
                textDecoration: "none",
                display: "inline-block",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: "900",
                  fontSize: "1.5rem",
                  letterSpacing: "0.2em",
                  background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                <img
                  src="/logo.png"
                  alt="NORYQ"
                  style={{
                    height: "50px",
                    width: "auto", // Maintains aspect ratio
                    display: "block",
                  }}
                />
              </span>
            </a>
            <p
              style={{
                color: "#6b7280",
                lineHeight: 1.7,
                fontSize: "0.875rem",
                marginBottom: "1.5rem",
              }}
            >
              Your AI-powered movie and TV show recommendation companion. Find
              your perfect watch based on your mood.
            </p>

            {/* Install CTA card */}
            <style>{`
              @media (max-width: 480px) {
                .noryq-install-card { padding: 1rem !important; }
                .noryq-install-card p { font-size: 0.88rem !important; }
                .noryq-install-label { display: none !important; }
              }
            `}</style>
            <div
              className="noryq-install-card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.1) 100%)",
                border: "1.5px solid #ede9fe",
                borderRadius: "16px",
                padding: "1.1rem 1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Label row */}
              <div
                className="noryq-install-label"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    fontSize: "0.68rem",
                    fontWeight: "700",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#7c3aed",
                  }}
                >
                  PWA · Free · No App Store Needed
                </span>
              </div>

              {/* Headline */}
              <p
                className="noryq-install-headline"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "0.95rem",
                  fontWeight: "700",
                  fontStyle: "italic",
                  color: "#1e1b4b",
                  lineHeight: 1.35,
                  margin: 0,
                }}
              >
                Take NORYQ everywhere.
                <br />
                <span
                  style={{
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Install it in one tap.
                </span>
              </p>

              {/* Button — full width, never overflows */}
              <div style={{ width: "100%", minWidth: 0 }}>
                <InstallAppButton />
              </div>
            </div>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Quick Links
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {quickLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink
                    href={link.href || "#"}
                    onClick={
                      link.view
                        ? (e) => {
                            e.preventDefault();
                            handleNavigation(link.view);
                          }
                        : undefined
                    }
                  >
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Legal
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {legalLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink href={link.href}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Stay Updated
              </h4>
            </div>
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.875rem",
                lineHeight: 1.7,
                marginBottom: "1rem",
              }}
            >
              Follow us for the latest recommendations and feature drops.
            </p>
            {/* Socials */}
            <div
              style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem" }}
            >
              {socials.map((s) => (
                <SocialIcon key={s.name} s={s} />
              ))}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background:
                  "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.1))",
                border: "1px solid #c4b5fd",
                borderRadius: "999px",
                padding: "0.4rem 1rem",
                marginBottom: "1.25rem",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#667eea,#764ba2)",
                }}
              />
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#7c3aed",
                  fontWeight: 600,
                }}
              >
                v1.0.0 — Live
              </span>
            </div>
          </div>
        </div>
        <div
          style={{
            height: "1px",
            background:
              "linear-gradient(90deg,transparent,#c4b5fd 40%,#a78bfa 60%,transparent)",
            marginBottom: "1.5rem",
          }}
        />
        <div
          className="noryq-footer-bottom"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
            © {currentYear} NORYQ. All rights reserved. Made with ❤️ in India
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              fontSize: "0.82rem",
              color: "#9ca3af",
              alignItems: "center",
            }}
          >
            <span>Powered by</span>
            <a
              href="https://vybex1.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <VybexLogo />
            </a>

            <span style={{ color: "#c4b5fd" }}>•</span>
            <span>v1.0.0</span>
          </div>
        </div>
        {/* <LegalNotice /> */}
      </div>
    </footer>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────
const WhyUs = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    document.title = "Why Us | NORYQ";
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        color: "#1e1b4b",
        fontFamily: "'DM Sans', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #f5f3ff; }
        ::-webkit-scrollbar-thumb { background: #a78bfa; border-radius: 2px; }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes float-blob { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-18px) scale(1.04)} }

        /* ── Responsive ─────────────────────────────────────────── */

        /* Hero stats card: stack on very small screens */
        .noryq-stats-card { display: flex; flex-wrap: wrap; }
        .noryq-stat-cell { flex: 1 1 140px; border-right: 1px solid #ede9fe; border-bottom: none; }
        @media (max-width: 480px) {
          .noryq-stat-cell { border-right: none !important; border-bottom: 1px solid #ede9fe; }
          .noryq-stat-cell:last-child { border-bottom: none; }
        }

        /* Hero section padding */
        @media (max-width: 640px) {
          .noryq-hero-inner { padding: 4rem 1.25rem 3.5rem !important; }
        }

        /* Problem section: 2-col → 1-col */
        .noryq-problem-grid { display: grid; grid-template-columns: 1fr 1.5fr; gap: 5rem; align-items: center; }
        @media (max-width: 860px) {
          .noryq-problem-grid { grid-template-columns: 1fr !important; gap: 2rem !important; }
        }

        /* Why Us cards: already auto-fit but fix card padding on mobile */
        @media (max-width: 480px) {
          .noryq-why-card { padding: 1.5rem !important; }
        }

        /* Who It's For: 2-col → 1-col, unstick heading */
        .noryq-whoisfor-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 5rem; align-items: start; }
        @media (max-width: 860px) {
          .noryq-whoisfor-grid { grid-template-columns: 1fr !important; gap: 2rem !important; }
          .noryq-whoisfor-heading { position: static !important; }
        }

        /* Benefits rows: fixed 160px label → flexible */
        .noryq-benefit-row { display: grid; grid-template-columns: 160px 1fr; gap: 1.25rem; align-items: start; padding: 1.75rem 0; border-bottom: 1px solid #f3f0ff; }
        @media (max-width: 600px) {
          .noryq-benefit-row { grid-template-columns: 1fr !important; gap: 0.4rem !important; padding: 1.25rem 0 !important; }
        }

        /* Comparison table: let it scroll horizontally */
        .noryq-table-wrap { overflow-x: auto; border-radius: 16px; border: 1.5px solid #ede9fe; background: #fff; box-shadow: 0 4px 32px rgba(102,126,234,0.06); }
        .noryq-table-wrap table { min-width: 520px; }

        /* Section paddings: reduce on mobile */
        @media (max-width: 640px) {
          .noryq-section { padding: 3.5rem 1.25rem !important; }
          .noryq-section-alt { padding: 3.5rem 1.25rem !important; }
        }

        /* Closing gradient section */
        @media (max-width: 640px) {
          .noryq-closing { padding: 4rem 1.25rem !important; }
          .noryq-closing-quote { font-size: 1.4rem !important; }
        }

        /* Footer grid: already auto-fit, just tighten gap on mobile */
        @media (max-width: 640px) {
          .noryq-footer-grid { gap: 2rem !important; }
          .noryq-footer-bottom { flex-direction: column !important; align-items: flex-start !important; gap: 0.5rem !important; }
        }

        /* Nav padding on mobile */
        @media (max-width: 480px) {
          .noryq-nav { padding: 0 1.25rem !important; }
        }

        /* Install card label: hide on very small */
        @media (max-width: 400px) {
          .noryq-install-label { display: none !important; }
          .noryq-install-card { padding: 1rem !important; }
        }
      `}</style>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav
        className="noryq-nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(250,250,250,0.88)",
          backdropFilter: "blur(18px)",
          borderBottom: "1px solid #ede9fe",
          padding: "0 2.5rem",
          height: "64px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <a href="/" style={{ textDecoration: "none" }}>
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: "900",
              fontSize: "1.45rem",
              letterSpacing: "0.2em",
              background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              cursor: "pointer",
              userSelect: "none",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <img
              src="/logo.png"
              alt="NORYQ"
              style={{
                height: "50px",
                width: "auto", // Maintains aspect ratio
                display: "block",
              }}
            />
          </span>
        </a>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#fff",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-15%",
            right: "-8%",
            width: "52vw",
            height: "52vw",
            maxWidth: "680px",
            maxHeight: "680px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.06) 50%, transparent 70%)",
            animation: "float-blob 8s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-10%",
            left: "-6%",
            width: "38vw",
            height: "38vw",
            maxWidth: "480px",
            maxHeight: "480px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(118,75,162,0.07) 0%, transparent 70%)",
            animation: "float-blob 11s ease-in-out infinite reverse",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle, #d8d0f8 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            opacity: 0.35,
          }}
        />

        <div
          className="noryq-hero-inner"
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "8rem 2rem 7rem",
            position: "relative",
            zIndex: 1,
            textAlign: "center",
          }}
        >
          {/* Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.12))",
              border: "1px solid #c4b5fd",
              borderRadius: "999px",
              padding: "0.4rem 1.1rem",
              marginBottom: "2.5rem",
              transform: heroVisible ? "translateY(0)" : "translateY(14px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.6s ease 0.1s, opacity 0.6s ease 0.1s",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                animation: "pulse-dot 1.8s infinite",
              }}
            />
            <span
              style={{
                fontSize: "0.74rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#7c3aed",
                fontWeight: 700,
              }}
            >
              Why Choose Us
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2.4rem, 6vw, 5rem)",
              fontWeight: "900",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#1e1b4b",
              marginBottom: "1.75rem",
              maxWidth: "800px",
              margin: "0 auto 1.75rem",
              transform: heroVisible ? "translateY(0)" : "translateY(22px)",
              opacity: heroVisible ? 1 : 0,
              transition:
                "transform 0.75s cubic-bezier(.22,1,.36,1) 0.2s, opacity 0.75s ease 0.2s",
            }}
          >
            {data.hero.tagline.split("deserves")[0]}
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              deserves
            </span>
            {data.hero.tagline.split("deserves")[1]}
          </h1>

          <p
            style={{
              fontSize: "clamp(1rem, 1.8vw, 1.15rem)",
              lineHeight: 1.8,
              color: "#6b7280",
              maxWidth: "540px",
              margin: "0 auto 4rem",
              transform: heroVisible ? "translateY(0)" : "translateY(18px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.7s ease 0.35s, opacity 0.7s ease 0.35s",
            }}
          >
            {data.hero.sub}
          </p>

          {/* Problem stats */}
          <div
            style={{
              display: "flex",
              gap: "0",
              justifyContent: "center",
              flexWrap: "wrap",
              background: "#fff",
              border: "1.5px solid #ede9fe",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 4px 32px rgba(102,126,234,0.07)",
              maxWidth: "700px",
              margin: "0 auto",
              transform: heroVisible ? "translateY(0)" : "translateY(18px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.7s ease 0.5s, opacity 0.7s ease 0.5s",
            }}
          >
            {data.problem.map((p, i) => (
              <div
                key={i}
                style={{
                  flex: "1 1 180px",
                  padding: "2rem 1.5rem",
                  textAlign: "center",
                  borderRight: i < 2 ? "1px solid #ede9fe" : "none",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "clamp(1.8rem,4vw,2.5rem)",
                    fontWeight: "900",
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1,
                    marginBottom: "0.5rem",
                  }}
                >
                  {p.stat}
                </div>
                <div
                  style={{
                    color: "#9ca3af",
                    fontSize: "0.78rem",
                    lineHeight: 1.5,
                    maxWidth: "140px",
                    margin: "0 auto",
                  }}
                >
                  {p.label}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            height: "2px",
            background:
              "linear-gradient(90deg,transparent,#c4b5fd 40%,#a78bfa 60%,transparent)",
          }}
        />
      </div>

      {/* ── THE PROBLEM ──────────────────────────────────────────────── */}
      <div
        className="noryq-section"
        style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
      >
        <FadeIn>
          <div
            className="noryq-problem-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.5fr",
              gap: "5rem",
              alignItems: "center",
            }}
          >
            <div>
              <SectionLabel text="The Problem" />
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(2rem, 3.5vw, 3rem)",
                  fontWeight: "900",
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  color: "#1e1b4b",
                }}
              >
                Every platform fights for your attention —{" "}
                <span
                  style={{
                    fontStyle: "italic",
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  not your satisfaction.
                </span>
              </h2>
            </div>
            <p style={{ color: "#4b5563", lineHeight: 1.85, fontSize: "1rem" }}>
              Netflix recommends what keeps you on Netflix. Prime pushes what
              Prime has. Their algorithms are designed to maximize{" "}
              <em>their</em> watch time, not to find what's best for{" "}
              <em>your</em> mood, your evening, your family. And Google? It
              gives you a list — not an answer. The result: 40 minutes of
              scrolling, frustration, and settling for something "good enough."
              You deserve better.
            </p>
          </div>
        </FadeIn>
      </div>

      {/* ── WHY US CARDS ─────────────────────────────────────────────── */}
      <div
        style={{
          background: "#f5f3ff",
          borderTop: "1px solid #ede9fe",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        <div
          className="noryq-section-alt"
          style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
        >
          <FadeIn>
            <SectionLabel text="What Makes Us Different" center />
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem,3.5vw,2.8rem)",
                fontWeight: "900",
                letterSpacing: "-0.03em",
                color: "#1e1b4b",
                textAlign: "center",
                marginBottom: "3.5rem",
              }}
            >
              Six reasons users don't go back to scrolling.
            </h2>
          </FadeIn>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.25rem",
            }}
          >
            {data.whyUs.map((item, i) => (
              <WhyUsCard key={i} item={item} delay={i * 0.07} />
            ))}
          </div>
        </div>
      </div>

      {/* ── WHO IT'S FOR ─────────────────────────────────────────────── */}
      <div
        className="noryq-section"
        style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
      >
        <div
          className="noryq-whoisfor-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "5rem",
            alignItems: "start",
          }}
        >
          <div>
            <FadeIn>
              <SectionLabel text="Who It's For" />
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(2rem,3.5vw,3rem)",
                  fontWeight: "900",
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  color: "#1e1b4b",
                  position: "sticky",
                  top: "5rem",
                }}
              >
                A perfect
                <br />
                match for
                <br />
                every viewer.
              </h2>
            </FadeIn>
          </div>
          <div style={{ borderTop: "1px solid #f3f0ff" }}>
            {data.benefits.map((b, i) => (
              <FadeIn key={i} delay={i * 0.07}>
                <div className="noryq-benefit-row" style={{}}>
                  <div
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "1rem",
                      fontStyle: "italic",
                      fontWeight: "700",
                      background: "linear-gradient(135deg,#667eea,#764ba2)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      paddingTop: "0.1rem",
                    }}
                  >
                    {b.who}
                  </div>
                  <p
                    style={{
                      color: "#4b5563",
                      lineHeight: 1.75,
                      fontSize: "0.95rem",
                      margin: 0,
                    }}
                  >
                    {b.benefit}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>

      {/* ── COMPARISON TABLE ─────────────────────────────────────────── */}
      <div
        style={{
          background: "#f5f3ff",
          borderTop: "1px solid #ede9fe",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        <div
          className="noryq-section-alt"
          style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
        >
          <FadeIn>
            <SectionLabel text="Competitive Edge" center />
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem,3.5vw,2.8rem)",
                fontWeight: "900",
                letterSpacing: "-0.03em",
                color: "#1e1b4b",
                textAlign: "center",
                marginBottom: "3rem",
              }}
            >
              How we compare.
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div
              className="noryq-table-wrap"
              style={{
                borderRadius: "16px",
                border: "1.5px solid #ede9fe",
                background: "#fff",
                boxShadow: "0 4px 32px rgba(102,126,234,0.06)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: "'DM Sans', sans-serif",
                  minWidth: "560px",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #ede9fe" }}>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "1.1rem 1.5rem",
                        color: "#9ca3af",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Feature
                    </th>
                    {["NORYQ ✦", "Netflix", "Google", "Letterboxd"].map(
                      (h, i) => (
                        <th
                          key={i}
                          style={{
                            padding: "1.1rem 1.25rem",
                            textAlign: "center",
                            fontSize: "0.82rem",
                            fontWeight: i === 0 ? "700" : "500",
                            color: i === 0 ? "#7c3aed" : "#6b7280",
                            background:
                              i === 0
                                ? "linear-gradient(135deg,rgba(102,126,234,0.04),rgba(118,75,162,0.06))"
                                : "transparent",
                            borderLeft: "1px solid #ede9fe",
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.vsCompetitors.map((row, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom:
                          i < data.vsCompetitors.length - 1
                            ? "1px solid #f9f7ff"
                            : "none",
                      }}
                    >
                      <td
                        style={{
                          padding: "0.95rem 1.5rem",
                          color: "#374151",
                          fontSize: "0.9rem",
                        }}
                      >
                        {row.feature}
                      </td>
                      {[
                        { val: row.us, isUs: true },
                        { val: row.netflix, isUs: false },
                        { val: row.google, isUs: false },
                        { val: row.letterboxd, isUs: false },
                      ].map((cell, j) => (
                        <td
                          key={j}
                          style={{
                            padding: "0.95rem 1.25rem",
                            textAlign: "center",
                            background:
                              j === 0
                                ? "linear-gradient(135deg,rgba(102,126,234,0.02),rgba(118,75,162,0.04))"
                                : "transparent",
                            borderLeft: "1px solid #f3f0ff",
                          }}
                        >
                          <CheckCell val={cell.val} isUs={cell.isUs} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ── PLANS ────────────────────────────────────────────────────── */}
      {/*<div
        className="noryq-section" style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
      >
        <FadeIn>
          <SectionLabel text="Access & Plans" center />
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.8rem,3.5vw,2.8rem)",
              fontWeight: "900",
              letterSpacing: "-0.03em",
              color: "#1e1b4b",
              textAlign: "center",
              marginBottom: "0.75rem",
            }}
          >
            Free to start. Worth upgrading.
          </h2>
          <p
            style={{
              textAlign: "center",
              color: "#6b7280",
              fontSize: "1rem",
              marginBottom: "3.5rem",
            }}
          >
            No paywall between you and great recommendations.
          </p>
        </FadeIn>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.25rem",
          }}
        >
          {data.monetisation.map((plan, i) => {
            const [hov, setHov] = useState(false);
            return (
              <FadeIn key={i} delay={i * 0.1}>
                <div
                  onMouseEnter={() => setHov(true)}
                  onMouseLeave={() => setHov(false)}
                  style={{
                    background: plan.highlight
                      ? "linear-gradient(135deg,#667eea,#764ba2)"
                      : "#fff",
                    border: plan.highlight
                      ? "none"
                      : hov
                        ? "1.5px solid #c4b5fd"
                        : "1.5px solid #ede9fe",
                    borderRadius: "20px",
                    padding: "2.25rem 2rem",
                    transition: "all 0.28s",
                    transform: hov ? "translateY(-4px)" : "none",
                    boxShadow: plan.highlight
                      ? "0 16px 48px rgba(118,75,162,0.3)"
                      : hov
                        ? "0 12px 40px rgba(118,75,162,0.1)"
                        : "0 2px 12px rgba(0,0,0,0.04)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {plan.highlight && (
                    <div
                      style={{
                        position: "absolute",
                        top: "1rem",
                        right: "1rem",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "999px",
                        padding: "0.2rem 0.7rem",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#fff",
                      }}
                    >
                      Most Popular
                    </div>
                  )}
                  <h3
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "1.35rem",
                      fontWeight: "900",
                      color: plan.highlight ? "#fff" : "#1e1b4b",
                      marginBottom: "0.85rem",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {plan.plan}
                  </h3>
                  <p
                    style={{
                      color: plan.highlight
                        ? "rgba(255,255,255,0.8)"
                        : "#6b7280",
                      lineHeight: 1.75,
                      fontSize: "0.92rem",
                      margin: 0,
                    }}
                  >
                    {plan.desc}
                  </p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
*/}
      {/* ── CLOSING STATEMENT ────────────────────────────────────────── */}
      <div
        className="noryq-closing"
        style={{
          background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
          position: "relative",
          overflow: "hidden",
          padding: "7rem 2rem",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-2rem",
            left: "2%",
            fontFamily: "'Playfair Display', serif",
            fontSize: "18rem",
            lineHeight: 1,
            color: "rgba(255,255,255,0.05)",
            fontWeight: "900",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          "
        </div>
        <FadeIn>
          <div
            style={{
              maxWidth: "720px",
              margin: "0 auto",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "1rem",
                marginBottom: "2rem",
              }}
            >
              <div
                style={{
                  width: "2rem",
                  height: "1px",
                  background: "rgba(255,255,255,0.4)",
                }}
              />
              <span
                style={{
                  fontSize: "0.73rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.75)",
                  fontWeight: 700,
                }}
              >
                The Bottom Line
              </span>
              <div
                style={{
                  width: "2rem",
                  height: "1px",
                  background: "rgba(255,255,255,0.4)",
                }}
              />
            </div>
            <p
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.6rem,3.5vw,2.6rem)",
                fontWeight: "700",
                fontStyle: "italic",
                lineHeight: 1.4,
                color: "#fff",
                letterSpacing: "-0.02em",
                marginBottom: "2rem",
              }}
            >
              "We don't just recommend what to watch. We understand why you're
              watching."
            </p>
            <p
              style={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "1rem",
                lineHeight: 1.8,
                maxWidth: "500px",
                margin: "0 auto 2.5rem",
              }}
            >
              That's the difference. That's why users come back. That's why they
              tell their families. And that's why we built this.
            </p>
            <button
              onClick={() => (window.location.href = "/")}
              style={{
                background: "#fff",
                color: "#7c3aed",
                border: "none",
                padding: "1rem 2.5rem",
                borderRadius: "12px",
                fontSize: "0.9rem",
                fontWeight: "700",
                letterSpacing: "0.06em",
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 36px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              Try NORYQ Free →
            </button>
          </div>
        </FadeIn>
      </div>

      <Footer />
    </div>
  );
};

export default WhyUs;
